import React, { useState, useCallback, useEffect, useRef } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import styled from 'styled-components'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import { RiZoomOutFill, RiZoomInFill } from 'react-icons/ri'
import { ScaleIcon } from '../Icons/ScaleIcon'
import { useAppointmentsAdministrationState } from '../../stores/Administration/AppointmentsAdministrationProvider'
import i18n from '../../translation/i18n'
import ScrollWrapper from '../Page/logix2020/ScrollWrapper'
import { MdOutlineBlock } from 'react-icons/md'
import { getIsoDate } from '../../utils/getIsoDate'

const StyledCalendarHeader = styled.div`
  height: 3rem;
  background-color: #3a3a38;
  color: white;
  font-size: 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0.8rem;
  z-index: 6;
  position: sticky;
  top: 0;
  border-radius: 5px 5px 0 0;

  .arrowIcon {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.4rem;
    display: flex;
    align-self: center;
    transition: color 0.15s ease;

    &:hover {
      color: white;
      cursor: pointer;
    }
  }

  .zoomIcon {
    font-size: 1.2rem;
    margin: 0 5px;
    color: rgba(255, 255, 255, 0.8);
    transition: color 0.15s ease;

    &:hover {
      color: white;
      cursor: pointer;
    }
  }
  @media (max-width: 800px) {
    font-size: 1rem !important;

    div {
      font-size: 1rem !important;
    }

    span:not(.bubble) {
      font-size: 1rem !important;
    }
  }
  @media (min-width: 1184px) and (max-width: 1280px) {
    font-size: 1rem !important;

    div,
    span:not(.bubble) {
      font-size: 1rem !important;
    }
  }
  @media (min-width: 701px) and (max-width: 674px) {
    font-size: 0.8rem !important;

    div,
    span:not(.bubble) {
      font-size: 0.8rem !important;
    }
  }
`

const StyledCalendarBody = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 5px 5px;
  overflow: hidden;
`

const StyledCalendarRow = styled.div`
  display: flex;
  border-top: 1px solid #f0f0f0;
  height: ${(props) => (props.isZoomedIn ? '180px' : '60px')};
  transition: background-color 0.1s ease;
  scroll-margin-top: 3rem;

  &:hover {
    background-color: #fafafa;
  }
`

const StyledHourBlock = styled.div`
  width: 3.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 500;
  color: #575756;
  flex-shrink: 0;
`

const StyledEventsBlock = styled.div`
  flex-grow: 1;
  position: relative;
  border-left: 1px solid #f0f0f0;
  background-color: ${(props) => (props.isLocked ? '#fff0f0' : 'transparent')};
  cursor: ${(props) => (props.isLocked ? 'not-allowed' : 'pointer')};

  display: flex;
  align-items: center;
  justify-content: center;

  .block-icon {
    color: #dc3545;
    font-size: 48px;
    position: absolute;
    opacity: 0.25;
  }
`

const StyledEvent = styled.div`
  position: absolute;
  height: ${(props) => props.eventDuration};
  width: calc(100% - 4px);
  left: 2px;
  top: ${(props) => props.eventStart};
  z-index: 5;
  background-color: ${(props) => (props.isLocked ? '#fecaca' : '#dbeafe')};
  color: ${(props) => (props.isLocked ? '#991b1b' : '#1e40af')};
  border-left: 3px solid ${(props) => (props.isLocked ? '#dc3545' : '#3b82f6')};
  border-radius: 3px;
  padding: 0 5px;
  font-size: ${(props) => props.fontSize};
  text-align: start;
  font-weight: 500;

  &:hover {
    cursor: ${(props) => (props.isLocked ? 'not-allowed' : 'pointer')};
    filter: brightness(0.93);
  }
`

const StyledScaleButton = styled.div`
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  width: 40px;
  border: none;
  background-color: transparent;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  path {
    fill: white;
  }

  .bubble {
    background: #3b82f6;
    font-size: 8px;
    border-radius: 50px;
    height: 13px;
    width: 13px;
    text-align: center;
    line-height: 1.2;
    color: white;
    position: absolute;
    display: flex;
    bottom: 9px;
    right: 1px;
    align-items: center;
    justify-content: center;
  }
`

const DailyAppointmentsOverview = (props) => {
  const { date, selectedDateHoursArray, showSimpleHeader, setSelectedAction } =
    props

  const {
    selectedDays,
    setAppointmentDetails,
    setSelectedDate,
    setShowDailyAppointmentsCompareModal,
    setShowOverviewOverlay,
    selectedDate,
  } = useAppointmentsAdministrationState()

  const [isZoomedIn, setIsZoomed] = useState(false)
  const firstEventRowRef = useRef(null)
  const scrollWrapperRef = useRef(null)
  const pendingScrollRef = useRef(null)

  const toggleZoom = () => {
    const el = scrollWrapperRef.current
    if (!el) {
      setIsZoomed((prev) => !prev)
      return
    }
    const currentRowHeight = isZoomedIn ? 180 : 60
    const newRowHeight = isZoomedIn ? 60 : 180
    pendingScrollRef.current = el.scrollTop * (newRowHeight / currentRowHeight)
    setIsZoomed((prev) => !prev)
  }

  useEffect(() => {
    if (pendingScrollRef.current !== null && scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollTop = pendingScrollRef.current
      pendingScrollRef.current = null
    }
  }, [isZoomedIn])

  useEffect(() => {
    return () => {
      selectedDateHoursArray.forEach((hour) => {
        hour.length = 0
      })
    }
  }, [date, selectedDateHoursArray])

  useEffect(() => {
    if (firstEventRowRef.current) {
      firstEventRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [date, selectedDateHoursArray])

  const formatSelectedDate = useCallback((dateString) => {
    if (!dateString) {
      return null
    }

    try {
      const dateToFormat =
        typeof dateString === 'string'
          ? dateString
          : getIsoDate(dateString).split('T')[0]

      const date = new Date(dateToFormat)

      if (isNaN(date.getTime())) {
        return null
      }
      return (
        <div style={{ color: 'white' }}>
          <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>
            {date.toLocaleString(i18n.language, {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
            })}
          </span>
        </div>
      )
    } catch (error) {
      console.error('Error formatting date:', error)
      return null
    }
  }, [])

  const createEventBubble = (
    event,
    startTime,
    endTime,
    indexRow,
    indexEvent
  ) => {
    let startHour = parseInt(startTime.substring(11, 13))
    let startMinute = parseInt(startTime.substring(14, 16))
    let endHour = parseInt(endTime.substring(11, 13))
    let endMinute = parseInt(endTime.substring(14, 16))

    let eventStart = isZoomedIn ? startMinute * 3 : startMinute
    let eventDuration = isZoomedIn
      ? (endHour * 60 + endMinute - (startHour * 60 + startMinute)) * 3
      : endHour * 60 + endMinute - (startHour * 60 + startMinute)
    let fontSize = eventDuration > 20 ? '12px' : '7px'

    // eventWidth and eventLeft can be used if we want to show the events as a scale ( staircase layout `-_ )
    // let eventWidth = 100 / selectedDateHoursArray[indexRow].length;
    // eventLeft = indexEvent * eventWidth;

    const popover = (
      <Popover id='popover-basic' style={{ pointerEvents: 'none' }}>
        <Popover.Header as='h3' style={{ backgroundColor: '#3a3a38', borderBottom: 'none' }}>
          {formatSelectedDate(selectedDate)}
        </Popover.Header>
        <Popover.Body>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {startTime.substring(11, 16)} - {endTime.substring(11, 16)}
            {event.isLocked && <MdOutlineBlock style={{ color: 'red', fontSize: '16px' }} />}
          </span>
        </Popover.Body>
      </Popover>
    )
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        key={indexEvent}
        placement='top'
        overlay={popover}
      >
        <StyledEvent
          key={indexEvent}
          fontSize={fontSize}
          eventStart={`${eventStart}px`}
          eventDuration={`${eventDuration}px`}
          onClick={() =>
            !showSimpleHeader && !event.isLocked && onEventClickHandler(event)
          }
          isLocked={event.isLocked}
        >
          {eventDuration >= 9 && (
            <span>
              {startTime.substring(11, 16)} - {endTime.substring(11, 16)}
              {event.isLocked && (
                <MdOutlineBlock style={{ color: 'red', fontSize: '32px', opacity: 0.4, marginLeft: '6px', verticalAlign: 'middle' }} />
              )}
            </span>
          )}
        </StyledEvent>
      </OverlayTrigger>
    )
  }

  const onEventClickHandler = (event) => {
    setShowOverviewOverlay()
    setSelectedAction('')
    setAppointmentDetails(event)
  }

  const goToPreviousDay = () => {
    let currentDateIndex = selectedDays.findIndex((day) =>
      day[0].includes(date)
    )
    if (currentDateIndex > 0) {
      let newDate = selectedDays[currentDateIndex - 1][0].slice(0, 10)
      setSelectedDate(newDate)
    }
  }

  const goToNextDay = () => {
    let currentDateIndex = selectedDays.findIndex((day) =>
      day[0].includes(date)
    )
    if (currentDateIndex < selectedDays.length - 1) {
      let newDate = selectedDays[currentDateIndex + 1][0].slice(0, 10)
      setSelectedDate(newDate)
    }
  }
  return (
    <ScrollWrapper ref={scrollWrapperRef} style={{ height: showSimpleHeader ? '75vh' : '410px' }}>
      <StyledCalendarHeader>
        <div>
          {isZoomedIn ? (
            <RiZoomOutFill
              className='zoomIcon'
              onClick={toggleZoom}
            />
          ) : (
            <RiZoomInFill
              className='zoomIcon'
              onClick={toggleZoom}
            />
          )}
        </div>
        {date && formatSelectedDate(date)}
        {!showSimpleHeader && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <StyledScaleButton
              onClick={() => setShowDailyAppointmentsCompareModal(true)}
            >
              <ScaleIcon />
              <span className='bubble'>{selectedDays?.length || 0}</span>
            </StyledScaleButton>
            <IoIosArrowBack
              className='arrowIcon mx-2'
              onClick={() => goToPreviousDay()}
            />
            <IoIosArrowForward
              className='arrowIcon'
              onClick={() => goToNextDay()}
            />
          </div>
        )}
      </StyledCalendarHeader>
      <StyledCalendarBody>
        {/* loop through hoursArray to get 24 hour rows */}
        {selectedDateHoursArray.map((hour, indexRow) => {
          const isLockedHour = hour.some((event) => event.isLocked)
          const isFirstEventRow = hour.length > 0 && selectedDateHoursArray.findIndex(h => h.length > 0) === indexRow
          return (
            <StyledCalendarRow key={indexRow} isZoomedIn={isZoomedIn} ref={isFirstEventRow ? firstEventRowRef : null}>
              <StyledHourBlock>
                <span>{`${indexRow < 10 ? '0' : ''}${indexRow}:00`}</span>
              </StyledHourBlock>
              <StyledEventsBlock>
                {hour.map((event, indexEvent) =>
                  createEventBubble(
                    event,
                    event.startTime,
                    event.endTime,
                    indexRow,
                    indexEvent
                  )
                )}
                {isLockedHour && <MdOutlineBlock className='block-icon' />}
              </StyledEventsBlock>
            </StyledCalendarRow>
          )
        })}
      </StyledCalendarBody>
    </ScrollWrapper>
  )
}

export default DailyAppointmentsOverview
